import { useRef } from 'react';
import { type LucideIcon } from 'lucide-react';
import { Card } from '@monksflow/monks-ui';
import { motion } from 'motion/react';
import { OptimizedImage } from '@/shared/ui';
import { eases } from '@/shared/constants/motion.constants';

import type { SpringboardCardProps } from './SpringboardCard.types';

// Safari needs full preload for reliable video playback on hover
const isSafari =
  typeof navigator !== 'undefined' && /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

const renderIcon = (icon: string | LucideIcon, title: string) => {
  if (typeof icon === 'string') {
    <OptimizedImage src={icon} alt={title} width={24} height={24} />;
  }
  const Icon = icon;
  return <Icon size={24} color="white" />;
};

// MFINS-3433 — designer feedback: the springboard cards were taking too long
// to settle. Three knobs were trimmed:
//   - `startDelay` (was 0.8s) — the first card now starts almost immediately.
//   - `STAGGER_STEP_S` (was 0.15s per card) — the cascade between cards is
//     tighter so the strip reads as a single move instead of one card at a
//     time.
//   - `y` tween duration (was 1s) — the slide-up settles in half the time.
// Opacity duration is left at 0.5s because it was already fine.
const STAGGER_STEP_S = 0.06;
const Y_DURATION_S = 0.5;

export const SpringboardCard = ({
  id,
  title,
  description,
  poster,
  video,
  icon,
  index,
  isInView,
  startDelay = 0.2,
  onClick,
}: SpringboardCardProps) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const playPromiseRef = useRef<Promise<void> | null>(null);

  const handleMouseEnter = () => {
    if (videoRef.current) {
      playPromiseRef.current = videoRef.current.play();
      playPromiseRef.current.catch(() => {});
    }
  };

  const handleMouseLeave = () => {
    if (videoRef.current) {
      const playPromise = playPromiseRef.current;
      if (playPromise) {
        playPromise
          .then(() => {
            videoRef.current?.pause();
          })
          .catch(() => {});
      } else {
        videoRef.current.pause();
      }
    }
  };

  const itemStartDelay = startDelay + index * STAGGER_STEP_S;

  return (
    <motion.div
      key={id}
      className="min-w-0"
      initial={{ opacity: 0.025, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0.025, y: 40 }}
      transition={{
        y: { duration: Y_DURATION_S, delay: itemStartDelay, ease: eases.expoOut },
        opacity: { duration: 0.5, delay: itemStartDelay, ease: eases.sineOut },
      }}
    >
      <Card
        className="relative w-full h-26 md:h-45 overflow-hidden shadow-none! rounded-lg p-0 gap-0 cursor-pointer"
        onClick={() => onClick(id)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* Background video */}
        <motion.video
          ref={videoRef}
          className="absolute inset-0 size-full object-cover pointer-events-none"
          initial={{ scale: 1.6 }}
          animate={isInView ? { scale: 1 } : { scale: 1.6 }}
          transition={{ duration: 1.8, delay: itemStartDelay, ease: eases.quartOut }}
          src={video}
          poster={poster}
          muted
          loop
          playsInline
          preload={isSafari ? 'auto' : 'none'}
        />

        {/* Overlay: icon badge + title + description */}
        <div className="absolute inset-0 flex flex-row justify-start md:flex-col md:justify-end gap-4 p-6">
          {/* Icon badge */}
          <div className="flex items-center justify-center size-13.5 rounded-sm bg-white/10 backdrop-blur-[10px] shrink-0">
            {renderIcon(icon, title)}
          </div>

          {/* Text */}
          <div className="flex flex-col">
            <span className="text-[30px] leading-10 font-medium tracking-[-0.025em] text-white">
              {title}
            </span>
            <span className="text-sm leading-5 text-white">{description}</span>
          </div>
        </div>
      </Card>
    </motion.div>
  );
};
